import React, { useState } from 'react';
import { Search as SearchIcon } from '@mui/icons-material';
import {
  searchLiveKnowledge,
  type LiveKnowledgeResult,
  type LiveKnowledgeType,
} from '../../services/apiService';

interface QuickTestBoxProps {
  /** Published objections and FAQs. With none, the check stays disabled. */
  liveAnswerCount: number;
}

const RESULTS_PER_TYPE = 3;
/** The backend refuses a longer query (400) for every type. */
const MAX_QUERY_LENGTH = 150;

const COVERAGE_SECTIONS: ReadonlyArray<{ type: LiveKnowledgeType; title: string; noun: string }> = [
  { type: 'objection', title: 'Objections', noun: 'objections' },
  { type: 'faq', title: 'FAQs', noun: 'FAQs' },
  { type: 'landmine', title: 'Landmines', noun: 'landmines' },
  { type: 'competitor', title: 'Competitors', noun: 'competitors' },
];

export const NOTHING_COVERS_THIS = 'Nothing covers this. Add an objection, FAQ, landmine or competitor.';

/** Keyword and meaning search alike return only real matches (meaning matches
 * are model-confirmed by the backend), so an empty result means "not covered". */
type SectionOutcome =
  | { status: 'failed' }
  | { status: 'searched'; results: LiveKnowledgeResult[] };

/** A type with no outcome yet is still searching. */
type Coverage = Partial<Record<LiveKnowledgeType, SectionOutcome>>;

const searchType = async (query: string, type: LiveKnowledgeType): Promise<SectionOutcome> => {
  try {
    const response = await searchLiveKnowledge(query, type, RESULTS_PER_TYPE);
    if (response.unavailableTypes?.includes(type)) return { status: 'failed' };
    return { status: 'searched', results: (response.results || []).slice(0, RESULTS_PER_TYPE) };
  } catch {
    return { status: 'failed' };
  }
};

/** Runs the rep's own live-call Search KB once per type, so the admin sees what
 * a rep would find mid-call. Unpublished draft testing lives only in the
 * onboarding review's whole-draft DraftKnowledgeSearch. */
const QuickTestBox: React.FC<QuickTestBoxProps> = ({ liveAnswerCount }) => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [inputError, setInputError] = useState<string | null>(null);

  const handleCheck = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setInputError('Type at least a couple of words to check.');
      return;
    }
    if (trimmed.length > MAX_QUERY_LENGTH) {
      setInputError(`Keep it under ${MAX_QUERY_LENGTH} characters.`);
      return;
    }
    setLoading(true);
    setInputError(null);
    setCoverage({});
    // Each type shows as soon as its own search answers: keyword types take
    // about 0.3 s, a sentence's objection/FAQ meaning search about 2 s.
    await Promise.all(COVERAGE_SECTIONS.map(async ({ type }) => {
      const outcome = await searchType(trimmed, type);
      setCoverage((previous) => ({ ...previous, [type]: outcome }));
    }));
    setLoading(false);
  };

  if (liveAnswerCount === 0) {
    return (
      <div className="rounded-xl border border-hairline bg-surface-subtle p-4">
        <h2 className="text-sm font-semibold text-ink">Check KB coverage</h2>
        <div className="mt-3 flex gap-2">
          <input aria-label="KB coverage check unavailable" disabled placeholder="Publish starter answers to check coverage" className="min-w-0 flex-1 rounded-lg border border-hairline bg-surface px-3 py-2 text-sm text-ink-muted" />
          <button type="button" disabled className="kb-button kb-button-secondary shrink-0 px-4 py-2 text-sm"><SearchIcon style={{ fontSize: '1.1rem' }} />Search</button>
        </div>
      </div>
    );
  }

  // Nothing shows until the first search answers, so a search whose four types
  // answer together never flashes four "Searching…" lines.
  const answered = coverage === null ? 0 : COVERAGE_SECTIONS.filter(({ type }) => coverage[type]).length;
  const allAnswered = answered === COVERAGE_SECTIONS.length;
  // A section shows while it is still searching, or when it found something or
  // its search failed: a failure must never read as "not covered", and an
  // empty one is noise.
  const shownSections = coverage === null ? [] : COVERAGE_SECTIONS.filter(({ type }) => {
    const outcome = coverage[type];
    return !outcome || outcome.status === 'failed' || outcome.results.length > 0;
  });

  return (
    <div className="kb-quick-test space-y-3">
      <div className="kb-quick-test-inner rounded-xl border border-hairline bg-surface p-4">
        <h2 className="text-sm font-semibold text-ink">Check KB coverage</h2>
        <form onSubmit={handleCheck} className="mt-3 flex gap-2" aria-label="Check KB coverage">
          <input aria-label="What a customer might say" type="text" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="e.g. Is this too expensive?" className="min-w-0 flex-1 rounded-lg border border-ink-border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-signature" />
          <button type="submit" disabled={loading} className="kb-button kb-button-secondary flex shrink-0 items-center gap-1.5 px-4 py-2 text-sm font-medium disabled:opacity-50"><SearchIcon style={{ fontSize: '1.1rem' }} />{loading ? 'Searching…' : 'Search'}</button>
        </form>
        {inputError && <p role="alert" className="mt-2 text-xs text-error">{inputError}</p>}
        {coverage && answered > 0 && !inputError && (
          <div className="mt-3 space-y-4" role="status" aria-live="polite">
            {allAnswered && shownSections.length === 0 ? <p className="text-sm text-ink-muted">{NOTHING_COVERS_THIS}</p> : shownSections.map(({ type, title, noun }) => {
              const outcome = coverage[type];
              return (
                <section key={type} aria-label={title}>
                  <h3 className="text-xs font-semibold text-ink-secondary">{title}</h3>
                  {!outcome ? (
                    <p className="mt-1 text-sm text-ink-muted">Searching {noun}…</p>
                  ) : outcome.status === 'failed' ? (
                    <p className="mt-1 text-sm text-ink-muted">Couldn't search {noun} right now</p>
                  ) : (
                    <div className="mt-2 space-y-2">
                      {outcome.results.map((result) => (
                        <div key={result._id} className="rounded-lg border border-hairline bg-surface-subtle p-3">
                          <p className="text-sm font-medium text-ink">{result.label}</p>
                          {result.answer && <p className="mt-1 line-clamp-2 whitespace-pre-wrap text-sm text-ink-secondary">{result.answer}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickTestBox;
